import React, { useState, useEffect, useCallback } from 'react';
import Editor from '@monaco-editor/react';
// The one confirmation dialog this modal has. SettingsModal also imports this tab; the cycle is
// safe because the binding is only read inside a render, after both modules have evaluated.
import { ConfirmDialog } from './SettingsModal';

// Default payloads use Responses API format (input, tools, reasoning, text.format)
// Legacy Chat Completions format (messages, response_format) is still supported for backward compatibility
const DEFAULT_STAGE1_PAYLOAD = {
  model: 'gpt-5.2',
  input: [
    {
      role: 'system',
      content: `You are an expert event marketing strategist with deep knowledge of audience psychology,
digital marketing, and Meta Ads best practices. Your role is to analyze events and create
comprehensive marketing strategies.

Focus on:
- Target audience identification and segmentation
- Key selling points and unique value propositions
- Competitive positioning
- Messaging themes that resonate emotionally
- Call-to-action strategies

Provide your analysis in a well-structured markdown report.`
    },
    {
      role: 'user',
      content: `Create a comprehensive marketing plan for the following event:

{{EVENT_DETAILS}}

Include:
1. Executive Summary
2. Target Audience Analysis (demographics, psychographics, interests)
3. Key Selling Points
4. Competitive Landscape
5. Messaging Strategy (tone, themes, emotional hooks)
6. Recommended Campaign Approach

Be specific and actionable. This plan will guide the creation of Meta Ads campaigns.`
    }
  ],
  tools: [{ type: 'web_search' }],
  reasoning: { effort: 'high' }
};

const DEFAULT_STAGE2_PAYLOAD = {
  model: 'gpt-5.2',
  input: [
    {
      role: 'system',
      content: `You are an expert Meta Ads Manager strategist who creates high-converting Facebook and
Instagram ad campaigns. You understand Meta's ad platform deeply, including campaign
objectives, ad set targeting options, ad creative best practices, and budget optimization.

Your role is to translate marketing plans into actionable Meta Ads campaign structures.`
    },
    {
      role: 'user',
      content: `Based on the marketing plan below, design a Meta Ads campaign structure.

## Marketing Plan
{{MARKETING_PLAN}}

## Event Details
{{EVENT_DETAILS}}

## Available Media Assets
{{MEDIA_ASSETS}}

Create a detailed campaign plan including:
1. Campaign Objective Selection (with rationale)
2. Ad Set Structure (audience targeting, placements, budget recommendations)
3. Ad Creative Recommendations (headlines, primary text variations, CTAs)
4. Media Asset Matching (which assets work best for which ad variations)
5. A/B Testing Suggestions

Be specific about targeting parameters and explain your choices.`
    }
  ],
  reasoning: { effort: 'high' }
};

const DEFAULT_STAGE3A_PAYLOAD = {
  model: 'gpt-5.2',
  input: [
    {
      role: 'system',
      content: `You are a technical assistant that converts marketing plans into structured JSON payloads
for the Meta Marketing API. You must output valid JSON that conforms to the official
Meta Marketing API field specifications provided.

For each field, provide:
- "value": The actual value to use (must match Meta API expected types)
- "confidence": "high", "medium", or "low"
- "reason": Brief explanation of why this value was chosen

IMPORTANT: Only use field names and values that are valid according to the Meta API
field reference provided. This ensures the payload can be submitted to Meta's API.

Always respond with a valid JSON object.`
    },
    {
      role: 'user',
      content: `Convert the following marketing and campaign plans into a structured JSON payload.

## Marketing Plan
{{MARKETING_PLAN}}

## Campaign Plan
{{CAMPAIGN_PLAN}}

## Event Details
{{EVENT_DETAILS}}

## Meta Marketing API Field Reference (Official)
{{META_API_FIELDS}}

Using the official Meta API field reference above, generate a JSON object with this structure:
{
  "campaign": {
    "name": {"value": "...", "confidence": "...", "reason": "..."},
    "objective": {"value": "OUTCOME_TRAFFIC", "confidence": "...", "reason": "..."},
    "special_ad_categories": {"value": [], "confidence": "...", "reason": "..."}
  },
  "ad_sets": [
    {
      "name": {"value": "...", "confidence": "...", "reason": "..."},
      "targeting": {
        "age_min": {"value": 18, "confidence": "...", "reason": "..."},
        "age_max": {"value": 65, "confidence": "...", "reason": "..."},
        "genders": {"value": [1, 2], "confidence": "...", "reason": "..."},
        "geo_locations": {"value": {"cities": []}, "confidence": "...", "reason": "..."},
        "interests": {"value": [], "confidence": "...", "reason": "..."}
      },
      "optimization_goal": {"value": "LINK_CLICKS", "confidence": "...", "reason": "..."},
      "billing_event": {"value": "IMPRESSIONS", "confidence": "...", "reason": "..."},
      "bid_strategy": {"value": "LOWEST_COST_WITHOUT_CAP", "confidence": "...", "reason": "..."}
    }
  ],
  "ad_creatives": [
    {
      "name": {"value": "...", "confidence": "...", "reason": "..."},
      "primary_text": {"value": "...", "confidence": "...", "reason": "..."},
      "headline": {"value": "...", "confidence": "...", "reason": "..."},
      "description": {"value": "...", "confidence": "...", "reason": "..."},
      "call_to_action": {"value": "LEARN_MORE", "confidence": "...", "reason": "..."},
      "link_url": {"value": "...", "confidence": "...", "reason": "..."}
    }
  ]
}`
    }
  ],
  text: { format: { type: 'json_object' } }
};

const DEFAULT_STAGE3B_PAYLOAD = {
  model: 'gpt-5.2',
  reasoning: { effort: 'medium' },
  max_output_tokens: 30000,
  input: [
    {
      role: 'system',
      content: `You are a senior Meta Ads strategist AND a teacher. You are writing detailed, teaching-style
explanations for each field selection in a structured campaign payload.

You will receive a condensed text summary of the campaign structure and the original Marketing Plan
and Meta Ads Strategy. Return a JSON map of explanations keyed by field path.

OUTPUT FORMAT:
{ "explanations": { "campaign.{key}.{api_key}": "explanation...", "ad_set.{key}.{api_key}": "...", "ad.{key}.{api_key}": "..." } }

Each explanation should follow this 5-part teaching template:
1) Why this choice (1-2 sentences)
2) Evidence from the plans (2-5 bullets with [MP §..] or [MAS §..] references)
3) How to reason it yourself (3-6 steps)
4) Tradeoffs + when to change it (1-3 bullets)
5) What to watch in Meta Ads Manager (1-2 bullets)

Do NOT return the full campaign structure — only the explanations map.
Return valid JSON only.`
    },
    {
      role: 'user',
      content: `Write detailed teaching-style explanations for each field in the structure below.

## Structure Summary
{{STRUCTURE_SUMMARY}}

## Marketing Plan (MP)
{{MARKETING_PLAN}}

## Meta Ads Strategy (MAS)
{{CAMPAIGN_PLAN}}

Return ONLY: { "explanations": { "path": "explanation text", ... } }
Use exact field paths from the summary. Return valid JSON only.`
    }
  ],
  text: { format: { type: 'json_object' } }
};

const DEFAULT_STAGE4_PAYLOAD = {
  model: 'gpt-5.2',
  reasoning: { effort: 'medium' },
  max_output_tokens: 30000,
  input: [
    {
      role: 'system',
      content: `You are a technical assistant that converts structured campaign recommendations into a payload format
that can be used to create Facebook campaign records in a database.

Your task is to transform the structured_payload (which contains recommendations with fields arrays)
into a simpler payload format that matches the expected structure for campaign creation.

OUTPUT FORMAT:
Your response must be valid JSON matching this exact structure:

{
  "event_data": {
    "event_id": "string",
    "name": "string",
    "start_date": "YYYY-MM-DD",
    "venue_name": "string"
  },
  "campaign": {
    "campaign_name": {
      "value": "string",
      "confidence": "high|medium|low",
      "assumptions": [],
      "reason": "string"
    },
    "campaign_objective": {
      "value": "OUTCOME_SALES|OUTCOME_TRAFFIC|OUTCOME_ENGAGEMENT|etc",
      "confidence": "high|medium|low",
      "assumptions": [],
      "reason": "string"
    },
    "campaign_start_at": {
      "value": "YYYY-MM-DD",
      "confidence": "high|medium|low",
      "assumptions": [],
      "reason": "string"
    },
    "campaign_end_at": {
      "value": "YYYY-MM-DD",
      "confidence": "high|medium|low",
      "assumptions": [],
      "reason": "string"
    },
    "campaign_lifetime_budget": {
      "value": number_in_cents,
      "confidence": "high|medium|low",
      "assumptions": [],
      "reason": "string"
    },
    "campaign_daily_budget": {
      "value": number_in_cents_or_null,
      "confidence": "high|medium|low",
      "assumptions": [],
      "reason": "string"
    },
    "campaign_buying_type": {
      "value": "AUCTION",
      "confidence": "high|medium|low",
      "assumptions": [],
      "reason": "string"
    },
    "campaign_special_ad_category": {
      "value": ["NONE"] or [],
      "confidence": "high|medium|low",
      "assumptions": [],
      "reason": "string"
    },
    "campaign_strategy_summary": {
      "value": "string",
      "confidence": "high|medium|low",
      "assumptions": [],
      "reason": "string"
    },
    "campaign_status": "draft",
    "ad_sets": [
      {
        "ad_set_name": { "value": "string", "confidence": "high", "assumptions": [], "reason": "string" },
        "billing_event": { "value": "impressions", "confidence": "high", "assumptions": [], "reason": "string" },
        "optimization_goal": { "value": "link_clicks|offsite_conversions|etc", "confidence": "high", "assumptions": [], "reason": "string" },
        "bid_strategy": { "value": "lowest_cost_without_cap", "confidence": "high", "assumptions": [], "reason": "string" },
        "budget": {
          "type": { "value": "lifetime|daily", "confidence": "high", "assumptions": [], "reason": "string" },
          "total_amount": { "value": number_or_null, "confidence": "medium", "assumptions": [], "reason": "string" },
          "daily_amount": { "value": number_or_null, "confidence": "medium", "assumptions": [], "reason": "string" }
        },
        "schedule": {
          "start_date": { "value": "YYYY-MM-DD", "confidence": "high", "assumptions": [], "reason": "string" },
          "end_date": { "value": "YYYY-MM-DD", "confidence": "high", "assumptions": [], "reason": "string" }
        },
        "age": {
          "min": { "value": number, "confidence": "medium", "assumptions": [], "reason": "string" },
          "max": { "value": number, "confidence": "medium", "assumptions": [], "reason": "string" }
        },
        "location": { "value": "City, State + radius", "confidence": "high", "assumptions": [], "reason": "string" },
        "destination_type": { "value": "website", "confidence": "high", "assumptions": [], "reason": "string" },
        "attribution_spec": { "value": [{ "event_type": "CLICK_THROUGH", "window_days": 7 }], "confidence": "high", "assumptions": [], "reason": "string" },
        "publisher_platforms": { "value": ["facebook"], "confidence": "high", "assumptions": [], "reason": "string" },
        "device_placements": { "value": ["mobile|website|etc"], "confidence": "high", "assumptions": [], "reason": "string" },
        "genders": { "value": [], "confidence": "high", "assumptions": [], "reason": "string" },
        "notes": { "value": "string", "confidence": "high", "assumptions": [], "reason": "string" },
        "ad_set_status": "draft",
        "ad_creatives": [
          {
            "ad_creative_name": { "value": "string", "confidence": "high", "assumptions": [], "reason": "string" },
            "ad_creative_cta_type": { "value": "LEARN_MORE|BUY_NOW|ORDER_NOW|etc", "confidence": "high", "assumptions": [], "reason": "string" },
            "ad_creative_link": { "value": "url", "confidence": "high", "assumptions": [], "reason": "string" },
            "ad_creative_primary_text": { "value": ["array", "of", "variations"], "confidence": "high", "assumptions": [], "reason": "string" },
            "ad_creative_headline": { "value": ["array", "of", "variations"], "confidence": "high", "assumptions": [], "reason": "string" },
            "ad_creative_description": { "value": ["array", "of", "variations"], "confidence": "high", "assumptions": [], "reason": "string" },
            "ad_creative_policy_safety_notes": { "value": "string_or_null", "confidence": "high", "assumptions": [], "reason": "string" },
            "ad_creative_notes": { "value": "string_or_null", "confidence": "high", "assumptions": [], "reason": "string" },
            "ad_name": { "value": "string", "confidence": "high", "assumptions": [], "reason": "string" }
          }
        ]
      }
    ]
  },
  "analytics_metadata": {
    "sources": [...],
    "event_analysis": {...},
    "conversion_goals": {...},
    "market_research": {...}
  }
}

KEY REQUIREMENTS:
1. Extract values from structured_payload.recommendations.campaign[].fields array (look for api_key like "campaign.name", "campaign.objective", etc.)
2. Extract values from structured_payload.recommendations.ad_sets[].fields array (look for api_key like "ad_set.name", "ad_set.age_min", etc.)
3. Extract values from structured_payload.recommendations.ad_sets[].ads[].fields array (look for api_key like "ad.name", "ad.copy.headline_variations", etc.)
4. Convert campaign.lifetime_budget from cents to the value (if it's 50000 cents, use 50000 - keep in cents)
5. Convert dates from ISO 8601 format (2026-01-01T00:00:00-05:00) to YYYY-MM-DD format
6. Map campaign.objective values: OUTCOME_SALES, OUTCOME_TRAFFIC, OUTCOME_ENGAGEMENT, etc.
7. Map optimization_goal values: link_clicks, offsite_conversions, etc.
8. Include analytics_metadata from structured_payload (sources, event_analysis, conversion_goals, market_research) - but DO NOT include ticket_math
9. If there are multiple campaigns in structured_payload.recommendations.campaign[], you MUST return an array of payloads (one per campaign)
10. Link ad_sets to campaigns using parent_campaign_key - each ad_set belongs to ONE campaign
11. For multiple campaigns: Return { "campaigns": [{...payload1...}, {...payload2...}] } where each payload has the full structure (event_data, campaign, analytics_metadata)
12. For single campaign: Return the payload object directly { "event_data": {...}, "campaign": {...}, "analytics_metadata": {...} }

IMPORTANT:
- Every field must have the structure: { "value": ..., "confidence": "...", "assumptions": [], "reason": "..." }
- Use the "reason" from the original field if available, otherwise provide a brief explanation
- Keep confidence levels from original fields when available
- Ensure all dates are in YYYY-MM-DD format
- Budget values should remain in cents (don't convert to dollars)
- Arrays for primary_text, headline, description should contain multiple variations`
    },
    {
      role: 'user',
      content: `Convert the following structured_payload into the payload format required for campaign creation.

## Event Details
{{EVENT_DETAILS}}

## Structured Payload (from Stage 3)
{{STRUCTURED_PAYLOAD}}

Requirements:
- Follow the exact structure shown in the system prompt
- Extract all values from the structured_payload's fields arrays
- Convert dates to YYYY-MM-DD format
- Keep budget values in cents
- Include analytics_metadata but exclude ticket_math
- Ensure all ad sets are properly linked to their parent campaign using parent_campaign_key

MULTIPLE CAMPAIGNS:
- Check structured_payload.recommendations.campaign[] - if it's an array with 2+ campaigns:
  * Return: { "campaigns": [payload1, payload2, ...] }
  * Each payload must have: event_data, campaign, analytics_metadata
  * Filter ad_sets: only include ad_sets where parent_campaign_key matches the campaign's campaign_key
  * Each campaign gets its own complete payload with only its associated ad_sets

SINGLE CAMPAIGN:
- If there's only one campaign in the array (or it's a single object):
  * Return the payload object directly: { "event_data": {...}, "campaign": {...}, "analytics_metadata": {...} }
  * Include all ad_sets (they all belong to this single campaign)

- Return valid JSON only`
    }
  ],
  text: { format: { type: 'json_object' } }
};

const STAGE_TABS = [
  { id: 'stage1', label: 'Stage 1: Marketing Plan', defaultPayload: DEFAULT_STAGE1_PAYLOAD },
  { id: 'stage2', label: 'Stage 2: Campaign Plan', defaultPayload: DEFAULT_STAGE2_PAYLOAD },
  { id: 'stage3a', label: 'Stage 3a: Structure', defaultPayload: DEFAULT_STAGE3A_PAYLOAD },
  { id: 'stage3b', label: 'Stage 3b: Explanations', defaultPayload: DEFAULT_STAGE3B_PAYLOAD },
  { id: 'stage4', label: 'Stage 4: Payload Conversion', defaultPayload: DEFAULT_STAGE4_PAYLOAD }
];

// Detects if payload is in Responses API format vs legacy Chat Completions format
const getPayloadFormat = (payloadString) => {
  try {
    const payload = JSON.parse(payloadString);
    if (payload.input || payload.text?.format) {
      return { format: 'Responses API', color: '#059669', bg: '#d1fae5' };
    }
    if (payload.messages || payload.response_format) {
      return { format: 'Chat Completions (legacy)', color: '#d97706', bg: '#fef3c7' };
    }
    return { format: 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
  } catch {
    return { format: 'Invalid JSON', color: '#dc2626', bg: '#fef2f2' };
  }
};

const AIConfigurationTab = ({ companySlug }) => {
  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopeUrl = (url) => {
    if (!companySlug) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}company_slug=${companySlug}`;
  };
  const [activeStage, setActiveStage] = useState('stage1');
  const [payloads, setPayloads] = useState({
    stage1: JSON.stringify(DEFAULT_STAGE1_PAYLOAD, null, 2),
    stage2: JSON.stringify(DEFAULT_STAGE2_PAYLOAD, null, 2),
    stage3a: JSON.stringify(DEFAULT_STAGE3A_PAYLOAD, null, 2),
    stage3b: JSON.stringify(DEFAULT_STAGE3B_PAYLOAD, null, 2),
    stage4: JSON.stringify(DEFAULT_STAGE4_PAYLOAD, null, 2)
  });
  const [originalPayloads, setOriginalPayloads] = useState({});
  const [backendDefaults, setBackendDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState(null);
  // Replaces a native browser confirm on Reset All — the in-modal dialog every confirm here uses.
  const [confirmResetAll, setConfirmResetAll] = useState(false);

  // Fetch existing settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(scopeUrl('/api/meta_ads_ai/settings'), {
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();

        // Store backend code defaults for "Reset to Default" functionality
        if (data.defaults) {
          setBackendDefaults({
            stage1: data.defaults.stage1,
            stage2: data.defaults.stage2,
            stage3a: data.defaults.stage3a,
            stage3b: data.defaults.stage3b,
            stage4: data.defaults.stage4
          });
        }

        const newPayloads = {
          stage1: data.stage1_api_payload || data.defaults?.stage1,
          stage2: data.stage2_api_payload || data.defaults?.stage2,
          stage3a: data.stage3a_api_payload || data.defaults?.stage3a,
          stage3b: data.stage3b_api_payload || data.defaults?.stage3b,
          stage4: data.stage4_api_payload || data.defaults?.stage4
        };
        setPayloads(newPayloads);
        setOriginalPayloads(newPayloads);
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateJSON = useCallback((value, stage) => {
    try {
      JSON.parse(value);
      setValidationErrors(prev => ({ ...prev, [stage]: null }));
      return true;
    } catch (e) {
      setValidationErrors(prev => ({ ...prev, [stage]: e.message }));
      return false;
    }
  }, []);

  const handleEditorChange = (value, stage) => {
    setPayloads(prev => ({ ...prev, [stage]: value }));
    validateJSON(value, stage);
    setSaveMessage(null);
  };

  const handleSave = async () => {
    // Validate all payloads before saving
    const allValid = STAGE_TABS.every(tab => validateJSON(payloads[tab.id], tab.id));

    if (!allValid) {
      setSaveMessage({ type: 'error', text: 'Please fix JSON validation errors before saving.' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      const response = await fetch(scopeUrl('/api/meta_ads_ai/settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({
          settings: {
            stage1_api_payload: payloads.stage1,
            stage2_api_payload: payloads.stage2,
            stage3a_api_payload: payloads.stage3a,
            stage3b_api_payload: payloads.stage3b,
            stage4_api_payload: payloads.stage4
          }
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setOriginalPayloads({ ...payloads });
        setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        setSaveMessage({ type: 'error', text: data.error || 'Failed to save settings.' });
      }
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Error saving settings: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    const defaultValue = backendDefaults[activeStage];
    if (defaultValue) {
      setPayloads(prev => ({ ...prev, [activeStage]: defaultValue }));
      setValidationErrors(prev => ({ ...prev, [activeStage]: null }));
      setSaveMessage(null);
    }
  };

  const handleResetAll = () => {
    setPayloads({ ...backendDefaults });
    setValidationErrors({});
    setSaveMessage(null);
    setConfirmResetAll(false);
  };

  const hasUnsavedChanges = () => {
    return STAGE_TABS.some(tab => payloads[tab.id] !== originalPayloads[tab.id]);
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Loading AI configuration...
      </div>
    );
  }

  return (
    <div className="ai-configuration-tab">
      {/* Info Banner */}
      <div style={{
        padding: '12px 16px',
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '6px',
        marginBottom: '16px',
        fontSize: '13px',
        color: '#1e40af'
      }}>
        <strong>AI Prompt Configuration</strong>
        <p style={{ margin: '8px 0 0 0' }}>
          Customize the prompts used for each stage of AI-powered ad generation.
          Use <code style={{ background: '#dbeafe', padding: '2px 4px', borderRadius: '3px' }}>{'{{VARIABLE_NAME}}'}</code> for
          dynamic values like <code style={{ background: '#dbeafe', padding: '2px 4px', borderRadius: '3px' }}>{'{{EVENT_DETAILS}}'}</code>,
          <code style={{ background: '#dbeafe', padding: '2px 4px', borderRadius: '3px' }}>{'{{MARKETING_PLAN}}'}</code>, etc.
        </p>
      </div>

      {/* Stage Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '16px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        {STAGE_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveStage(tab.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: 'transparent',
              borderBottom: activeStage === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeStage === tab.id ? '#3b82f6' : '#6b7280',
              fontSize: '13px',
              fontWeight: activeStage === tab.id ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
            {validationErrors[tab.id] && (
              <span style={{ color: '#dc2626', marginLeft: '6px' }}>!</span>
            )}
          </button>
        ))}
      </div>

      {/* Format Indicator */}
      {(() => {
        const formatInfo = getPayloadFormat(payloads[activeStage]);
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '8px'
          }}>
            <span style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '500',
              background: formatInfo.bg,
              color: formatInfo.color
            }}>
              Format: {formatInfo.format}
            </span>
          </div>
        );
      })()}

      {/* Editor */}
      <div style={{
        border: validationErrors[activeStage] ? '2px solid #dc2626' : '1px solid #e5e7eb',
        borderRadius: '6px',
        overflow: 'hidden',
        marginBottom: '12px'
      }}>
        <Editor
          height="350px"
          language="json"
          theme="vs-light"
          value={payloads[activeStage]}
          onChange={(value) => handleEditorChange(value, activeStage)}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true,
            tabSize: 2,
            formatOnPaste: true
          }}
        />
      </div>

      {/* Validation Error */}
      {validationErrors[activeStage] && (
        <div style={{
          padding: '10px 12px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#dc2626'
        }}>
          <strong>JSON Error:</strong> {validationErrors[activeStage]}
        </div>
      )}

      {/* Save Message */}
      {saveMessage && (
        <div style={{
          padding: '10px 12px',
          background: saveMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${saveMessage.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '13px',
          color: saveMessage.type === 'success' ? '#166534' : '#dc2626'
        }}>
          {saveMessage.text}
        </div>
      )}

      {/* Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '12px',
        borderTop: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleResetToDefault}
            style={{
              padding: '8px 14px',
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            Reset Stage to Default
          </button>
          <button
            onClick={() => setConfirmResetAll(true)}
            style={{
              padding: '8px 14px',
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            Reset All
          </button>
        </div>

        <button
          onClick={handleSave}
          disabled={saving || Object.values(validationErrors).some(e => e)}
          style={{
            padding: '8px 20px',
            background: saving ? '#9ca3af' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges() && !saveMessage && (
        <div style={{
          marginTop: '12px',
          fontSize: '12px',
          color: '#f59e0b',
          fontStyle: 'italic'
        }}>
          You have unsaved changes.
        </div>
      )}

      {confirmResetAll && (
        <ConfirmDialog
          title="Reset all stages to default?"
          body="This discards any unsaved changes in every stage editor."
          cancelLabel="Cancel"
          confirmLabel="Reset all"
          onCancel={() => setConfirmResetAll(false)}
          onConfirm={handleResetAll}
        />
      )}
    </div>
  );
};

export default AIConfigurationTab;
